import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { NotebookPen, ShieldCheck, Info, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RAILS = [
  { id: "ach", label: "ACH transfer", method: "bank", note: "Bank · USD" },
  { id: "wire", label: "Wire transfer", method: "bank", note: "Bank · USD" },
  { id: "usdc", label: "USDC", method: "crypto", note: "Stablecoin · on-chain" },
  { id: "usdt", label: "USDT", method: "crypto", note: "Stablecoin · on-chain" },
];

const Wallet = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState({ amount: "", currency: "USD", rail: "ach", destination: "" });
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const [w, p] = await Promise.all([api.get("/wallet"), api.get("/payouts")]);
      // Defensive: backend should always return these, but guard against shape drift.
      setWallet({
        balances: w.data?.balances ?? {},
        recent_entries: w.data?.recent_entries ?? [],
      });
      setPayouts(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load wallet";
      console.error("Failed to load wallet:", e);
      setLoadError(msg);
      toast.error(msg);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rail = RAILS.find((r) => r.id === form.rail);

  const submit = async () => {
    if (!form.amount) return toast.error("Enter an amount");
    setBusy(true);
    try {
      await api.post("/payouts", {
        amount: Number(form.amount),
        currency: form.currency,
        method: rail.method,
        rail: form.rail,
        destination: form.destination,
      });
      toast.success("Payout requested");
      setForm({ ...form, amount: "", destination: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return (
    <div className="p-10 text-sm" data-testid="wallet-load-error">
      <div className="text-red-400 mb-2">Could not load wallet.</div>
      <div className="text-zinc-500 text-xs mb-4">{loadError}</div>
      <button
        onClick={load}
        data-testid="wallet-retry-btn"
        className="bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200"
      >Retry</button>
    </div>
  );
  if (!wallet) return <div className="p-10 text-zinc-500 text-sm" data-testid="wallet-loading">Loading…</div>;

  return (
    <div data-testid="wallet-page">
      <PageHeader
        overline="Treasury · Non-custodial"
        title="Wallet ledger"
        description="A read-only mirror of invoices paid on-chain. Quill never holds your funds — every payment lands directly in the wallet address you control."
      />

      {/* Non-custodial honesty banner */}
      <div
        className="mx-8 mt-2 mb-2 border border-[#2c241e] bg-[#0E0E0E] p-4 flex items-start gap-3"
        data-testid="non-custodial-banner"
      >
        <ShieldCheck size={16} className="mt-0.5 text-emerald-400 shrink-0" />
        <div className="text-xs text-zinc-400 leading-relaxed flex-1">
          <span className="text-zinc-200 font-medium">Non-custodial by design.</span>{" "}
          The numbers below are an{" "}
          <span className="text-zinc-200">informational ledger</span> of invoices
          marked paid on-chain. Quill does <span className="text-zinc-200">not</span> hold,
          custody, or move your funds. Client payments land in the external wallet
          address you set in{" "}
          <a href="/settings" className="underline hover:text-white inline-flex items-center gap-1">
            Settings <ExternalLink size={10} />
          </a>
          . "Log a transfer" entries below are bookkeeping for off-platform moves you initiate yourself.
        </div>
      </div>

      {/* Connected payout wallet pill */}
      {user?.crypto_address && (
        <div className="mx-8 mb-4 flex flex-wrap items-center gap-2" data-testid="connected-wallet-pill">
          <div className="inline-flex items-center gap-2 border border-[#2c241e] bg-[#0A0A0A] px-3 py-1.5 text-[11px]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-zinc-500 uppercase tracking-[0.18em]">Receiving wallet</span>
            <span className="font-mono text-zinc-300">
              {user.crypto_address.slice(0, 6)}…{user.crypto_address.slice(-4)}
            </span>
          </div>
          <div
            className="inline-flex items-center gap-1.5 border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-400"
            data-testid="self-custody-badge"
            title="Quill never holds, custodies, or has access to your private keys or funds."
          >
            <ShieldCheck size={11} />
            Self-custody · Your keys, your funds
          </div>
        </div>
      )}

      {/* Balances */}
      <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#2c241e]">
        {(Object.keys(wallet.balances).length > 0 ? Object.entries(wallet.balances) : [["USD", 0]]).map(([ccy, v]) => (
          <div key={ccy} className="p-6 border-r border-b border-[#2c241e]" data-testid={`balance-${ccy}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-1.5">
              Ledger · {ccy}
              <span
                title="Sum of invoices marked paid on-chain in this currency. Funds are in your external wallet, not held by Quill."
                className="text-zinc-600 hover:text-zinc-300 cursor-help"
                data-testid={`ledger-tooltip-${ccy}`}
              >
                <Info size={11} />
              </span>
            </div>
            <div className="mt-3 font-mono text-3xl tracking-tight text-white">{fmtMoney(v, ccy)}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-600">
              Informational only
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 md:p-8 grid md:grid-cols-12 gap-8">
        {/* Bookkeeping form */}
        <section className="md:col-span-5 border border-[#2c241e] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Log a transfer</div>
          <div className="text-[10px] text-zinc-600 mb-5 leading-relaxed">
            Bookkeeping only — record a transfer you've already made off-platform
            so your ledger stays balanced. Quill never moves money on your behalf.
          </div>
          <div className="space-y-4">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Category</div>
              <div className="grid grid-cols-2 gap-2">
                {RAILS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setForm({ ...form, rail: r.id })}
                    data-testid={`rail-${r.id}`}
                    className={`p-3 text-left border text-sm ${
                      form.rail === r.id ? "border-white bg-[#241c17]" : "border-[#2c241e] hover:border-zinc-500"
                    }`}
                  >
                    <div className="text-white">{r.label}</div>
                    <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-[0.18em]">{r.note}</div>
                  </button>
                ))}
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Amount</div>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="payout-amount-input"
                  className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Currency</div>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  data-testid="payout-currency-select"
                  className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white">
                  {Object.keys(wallet.balances).length ? Object.keys(wallet.balances).map(c => <option key={c}>{c}</option>) : <option>USD</option>}
                </select>
              </label>
            </div>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
                {rail.method === "crypto" ? "Destination wallet address" : "Destination bank account"}
              </div>
              <input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })}
                placeholder={rail.method === "crypto" ? "0x..." : "•••• 4242"}
                data-testid="payout-destination-input"
                className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white" />
              <div className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">
                For your records — Quill stores this string but never sends to it.
              </div>
            </label>

            <button onClick={submit} disabled={busy} data-testid="request-payout-btn"
              className="w-full bg-white text-black text-sm font-medium px-4 py-2.5 rounded-md hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <NotebookPen size={14} strokeWidth={2.5} /> Log transfer
            </button>
          </div>
        </section>

        {/* History */}
        <section className="md:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4 flex items-center gap-1.5">
            Transfer log
            <span
              title="A journal of off-platform transfers you've logged. Status reflects your own attestation — Quill does not initiate or verify these moves."
              className="text-zinc-600 hover:text-zinc-300 cursor-help"
              data-testid="transfer-log-tooltip"
            >
              <Info size={11} />
            </span>
          </div>
          <div className="border border-[#2c241e]">
            {payouts.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm" data-testid="payouts-empty">No transfers logged yet.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#2c241e]">
                    <th className="p-4 font-normal">Date</th>
                    <th className="p-4 font-normal">Category</th>
                    <th className="p-4 font-normal text-right">Amount</th>
                    <th className="p-4 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p, i) => (
                    <tr key={p.payout_id || `p-${i}`} className="border-b border-[#2c241e] last:border-b-0" data-testid={`payout-row-${p.payout_id || i}`}>
                      <td className="p-4 text-sm text-zinc-400">{fmtDate(p.created_at)}</td>
                      <td className="p-4 text-sm uppercase font-mono">{p.rail || "—"}</td>
                      <td className="p-4 text-right font-mono text-sm">{fmtMoney(p.amount ?? 0, p.currency || "USD")}</td>
                      <td className="p-4 text-xs uppercase tracking-[0.18em] text-amber-400">{p.status || "pending"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-8 mb-4">Recent ledger entries</div>
          <div className="border border-[#2c241e]">
            {wallet.recent_entries.length === 0 ? (
              <div className="p-4 md:p-8 text-center text-zinc-500 text-sm" data-testid="ledger-empty">No activity yet.</div>
            ) : (
              <table className="w-full">
                <tbody>
                  {wallet.recent_entries.map((e, i) => (
                    <tr key={e.entry_id || `e-${i}`} className="border-b border-[#2c241e] last:border-b-0">
                      <td className="p-3 text-sm">{(e.source || "entry").replace(/_/g, " ")}</td>
                      <td className="p-3 text-xs text-zinc-500 text-right">{fmtDate(e.created_at)}</td>
                      <td className={`p-3 text-right font-mono text-sm ${e.type === "credit" ? "text-emerald-400" : "text-zinc-300"}`}>
                        {e.type === "credit" ? "+" : "−"}{fmtMoney(e.amount ?? 0, e.currency || "USD")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Wallet;
